import { useEffect, useRef, useState } from "react";
import { Loader2, SendHorizontal } from "lucide-react";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { GlobalFormSchema } from "~/scheme/global_form_schema";
import { parseWithZod } from "@conform-to/zod";
import {
  FetcherWithComponents,
  useFetcher,
  useNavigate,
} from "@remix-run/react";
import {
  TELE_BOT_TOKEN,
  TELE_CH_ID,
  WHATSAPP_GREETING,
  WHATSAPP_NUM,
} from "~/assets/profile/profile";
import { Text4 } from "../typographies/Texts";
import { Heading2 } from "../typographies/Headings";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SchemaLandingForm } from "~/scheme/schema_landing_form";
import clsx from "clsx";
import { ScrollArea } from "@radix-ui/react-scroll-area";

interface WhatsappGlobalInterface {
  imageUrl?: string;
  classnameButton?: string;
  classnameText?: string;
  currentPage?: string | undefined;
}
interface WhatsappGlobalProps {
  whatsappTextImage?: WhatsappGlobalInterface[];
  buttonText?: string;
  currentPage?: string;
  classnameButton?: string;
  classnameText?: string;
  showGif?: boolean; // ✅ opsi baru
}

export default function GlobalContactWhatsappFormLanding({
  buttonText = "Whatsapp Admin",
  currentPage,
  classnameButton,
  classnameText,
  showGif = true, // ✅ default tampilkan GIF
}: WhatsappGlobalProps) {
  const fetcher: FetcherWithComponents<any> = useFetcher();
  const [isOpen, setIsOpen] = useState(false);
  const [form, fields] = useForm({
    shouldValidate: "onBlur",
    onValidate({ formData }) {
      const schema = SchemaLandingForm;
      return parseWithZod(formData, { schema });
    },
  });

  const [inputWhatsapp, setInputWhatsapp] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [selectedBudget, setSelectedBudget] = useState("");
  const [selectedTimeline, setSelectedTimeline] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const closeModal = () => {
    setShowModal(false);
    // navigate("/jasa-bangun-rumah-di-bekasi-landing-terimakasih"); // Navigate to the home page
    // handleSendToWhatsApp(inputWhatsapp);
    setInputWhatsapp("");
    setSelectedBudget("");
    setSelectedTimeline("");
  };

  useEffect(() => {
    if (fetcher?.data?.msg === "success") {
      setShowModal(true);
    }
  }, [fetcher]);

  type FormErrors = {
    message: string;
    whatsapp: string[];
    selectedBudget: string;
    selectedTimeline: string;
  };

  const [formErrors, setFormErrors] = useState<FormErrors>({
    selectedBudget: "Budget wajib diisi",
    selectedTimeline: "Timeline wajib diisi",
    message: "Pesan wajib diisi",
    whatsapp: [
      "Whatsapp wajib diisi, agar kami dapat segera membalas pesan penting Anda.",
    ], // Default error for WhatsApp
  });
  useEffect(() => {
    const isValid =
      inputWhatsapp.trim() !== "" &&
      inputMessage.trim() !== "" &&
      selectedBudget !== "" &&
      selectedTimeline !== "" &&
      formErrors.whatsapp.length === 0 &&
      formErrors.message === "" &&
      formErrors.selectedBudget === "" &&
      formErrors.selectedTimeline === "";

    setIsFormValid(isValid);
  }, [
    inputWhatsapp,
    inputMessage,
    selectedBudget,
    selectedTimeline,
    formErrors.whatsapp,
    formErrors.message,
    formErrors.selectedBudget,
    formErrors.selectedTimeline,
  ]);

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // Reset form errors initially
    navigate("/jasa-bangun-rumah-di-bekasi-landing-terimakasih"); // Navigate to the home page
    handleSendToWhatsApp(inputWhatsapp);
    setFormErrors({
      selectedBudget: "",
      selectedTimeline: "",
      message: "",
      whatsapp: [],
    });

    // Prepare form data
    const formData = {
      whatsapp: inputWhatsapp,
      message: inputMessage,
      budget: selectedBudget,
      timeline: selectedTimeline,
    };

    // Validate form data
    const validation = GlobalFormSchema.safeParse(formData);

    if (!validation.success) {
      const errors = validation.error.format();
      // Set form errors based on validation results
      setFormErrors((prev) => ({
        ...prev,
        whatsapp: errors.whatsapp?._errors?.[0]
          ? [errors.whatsapp?._errors?.[0]]
          : [],
        message: "",
      }));

      return;
    }

    // If validation passes, submit the form
    fetcher.submit(event.currentTarget.form);
  };

  const isSubmitDisabled =
    !selectedBudget.trim() ||
    !selectedTimeline.trim() ||
    !inputMessage.trim() ||
    !inputWhatsapp.trim() ||
    !!formErrors.whatsapp.length;
  const pageRef = useRef<string | undefined>(currentPage);

  useEffect(() => {
    if (currentPage) {
      pageRef.current = currentPage;
    }
  }, [currentPage]);

  const handleSendToWhatsApp = (inputedWhatsapp: string) => {
    const now = new Date();
    const date = now.toLocaleDateString();
    const time = now.toLocaleTimeString();

    // Pesan untuk admin
    const adminMessage = `Halo Admin arsinesia.com, saya mau konsultasi`;
    const adminWhatsApp = WHATSAPP_NUM;

    // Buka chat ke Admin
    window.open(
      `https://wa.me/${adminWhatsApp}?text=${encodeURIComponent(adminMessage)}`,
      "_blank"
    );

    // --- Normalisasi nomor WhatsApp user ---
    let phone = inputedWhatsapp
      .replace(/\D/g, "") // hapus semua karakter non-digit
      .trim();

    if (phone.startsWith("0")) {
      phone = "62" + phone.slice(1);
    }

    // Link WhatsApp user
    const whatsappLink = `https://wa.me/${phone}`;
    console.log(
      "🔍 Using pageRef.current in Telegram Message:",
      pageRef.current
    );

    // Pesan untuk Telegram
    const telegramMessage = `🔴🔴🔴IKLAN GOOGLE
- Tanggal: ${date}
- Jam: ${time}
- Whatsapp: ${inputedWhatsapp}
- Link Whatsapp: ${whatsappLink}
- Budget: ${selectedBudget}
- Rencana Bangun: ${selectedTimeline}
- Pesan: ${inputMessage}`;

    console.log("📩 Sending to Telegram:", telegramMessage);

    // Kirim ke Telegram
    fetch(`https://api.telegram.org/bot${TELE_BOT_TOKEN}/sendMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chat_id: TELE_CH_ID,
        text: telegramMessage,
      }),
    })
      .then((res) => res.json())
      .then((data) => console.log("✅ Message sent to Telegram:", data))
      .catch((err) => console.error("❌ Error sending to Telegram:", err));
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxCharacters = 200; // Maximum allowed characters
  // Function to adjust the height of the textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // Reset the height to auto to calculate the new height
      textareaRef.current.style.height = "auto";
      // Set the height to the scrollHeight (content height)
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const sheetRef = useRef<HTMLDivElement>(null);
  const adjustSheetHeight = () => {
    if (sheetRef.current) {
      const contentHeight = sheetRef.current.scrollHeight;
      sheetRef.current.style.height = `${contentHeight}px`;
    }
  };

  // Adjust the height of the Sheet whenever the content changes
  useEffect(() => {
    adjustSheetHeight();
  }, [inputWhatsapp, inputMessage, formErrors, showModal]);
  const getFormattedDate = () => {
    const date = new Date(); // Get the current date
    const day = date.getDate(); // Get the day of the month (1-31)
    const month = date.toLocaleString("id-ID", { month: "long" }); // Get the full month name in Indonesian
    const year = date.getFullYear(); // Get the full year (e.g., 2023)

    return `${day} ${month} ${year}`; // Format the date as "Tanggal [day] [month] [year]"
  };
  const getFormattedTime = () => {
    const date = new Date(); // Get the current date and time
    const hours = date.getHours(); // Get the current hour (0-23)
    const minutes = date.getMinutes(); // Get the current minute (0-59)

    // Format hours and minutes to always be two digits (e.g., "09:05")
    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`; // Format the time as "HH:MM"
  };
  return (
    <>
      {/* Floating Button */}
      <div className={clsx("w-full py-[1rem] rounded-lg")}>
        <Button
          onClick={() => setIsOpen(true)}
          className={clsx(
            "w-full rounded-lg hover:bg-green-600 flex items-center gap-2",
            classnameButton // ✅ Tambahan class opsional
          )}
        >
          {showGif && (
            <img
              className="w-[2rem] h-[2rem]"
              src="https://ik.imagekit.io/metame/saranabisnis_jasa-pendirian-pt-di-semarang/whatsapp-gif.gif?updatedAt=1735835712357"
              alt="WhatsApp logo"
              loading="lazy"
            />
          )}
          <div className="flex flex-row gap-1">
            <p
              className={clsx(
                "text-md font-semibold text-black",
                classnameText
              )}
            >
              {buttonText}
            </p>
          </div>
        </Button>
      </div>

      {/* Sheet (Drawer) */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          ref={sheetRef}
          className="bg-white self-end rounded-lg w-full lg:w-fit flex flex-col overflow-y-auto"
          style={{ height: "auto", maxHeight: "90vh" }}
        >
          <SheetTitle>
            <div className="sticky top-0 z-10">
              <img
                src="https://ik.imagekit.io/didigi/bangun-rumah-di-bekasi/jasa-bangun-rumah-di-bekasi_arsinesia_banner-landing-form.webp"
                alt="Banner Arsinesia"
                className="w-full object-cover"
              />
            </div>
          </SheetTitle>
          {/* Banner Gambar Full Lebar */}

          {/* Content Berpadding */}
          <div className="px-[1rem] pt-[1rem] flex flex-col flex-1">
            <fetcher.Form
              method="POST"
              {...getFormProps(form)}
              className="flex-1 flex flex-col"
            >
              <input type="hidden" name="_action" value="penawaran" />
              <div className="typo-light-primary w-full flex flex-col gap-3 text-start">
                <label
                  htmlFor="message"
                  className="text-sm font-medium text-slate-900"
                >
                  Whatsapp Anda
                </label>
                <Input
                  placeholder="contoh : 08123456789"
                  {...getInputProps(fields.whatsapp, { type: "number" })}
                  id="whatsapp"
                  value={inputWhatsapp}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputWhatsapp(value);

                    // Validate the Whatsapp number
                    const errors: string[] = [];
                    if (!value.startsWith("08")) {
                      errors.push(
                        'Format nomor salah, awali dengan "08", contoh: 085123123123.'
                      );
                    }
                    if (value.length < 9) {
                      errors.push(
                        "Jumlah nomor whatsapp terlalu pendek, Isi dengan nomor whatsapp aktif."
                      );
                    }
                    if (value.length > 14) {
                      errors.push(
                        "Jumlah nomor whatsapp terlalu panjang, Isi dengan nomor whatsapp aktif."
                      );
                    }

                    // Update form errors for whatsapp
                    setFormErrors((prev) => ({
                      ...prev,
                      whatsapp: errors, // Assign the array of errors
                    }));
                  }}
                  className={`-mt-2 text-slate-900 h-[2.7rem] w-full p-2 border rounded-md focus:ring-light-primary focus:border-light-primary ${
                    formErrors.whatsapp.length > 0 ? "border-red-500 " : ""
                  }`}
                  required
                />
                {/* Display Errors for WhatsApp */}
                {formErrors.whatsapp.length > 0 && (
                  <ul className="-mt-2 ml-1 text-red-500 text-[12px] list-disc list-inside">
                    {formErrors.whatsapp.map((error, index) => (
                      <li key={index} className="text-red-400">
                        {error}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Input Budget */}
                <label
                  htmlFor="budget"
                  className="pt-[0.5rem] text-sm font-medium text-slate-900"
                >
                  Berapa budget yang Anda siapkan?
                </label>
                <input
                  type="hidden"
                  name="budget"
                  defaultValue={selectedBudget}
                />
                <Select
                  value={selectedBudget}
                  onValueChange={(value) => {
                    setSelectedBudget(value);

                    // Hapus error ketika sudah memilih
                    if (formErrors.selectedBudget.length > 0) {
                      setFormErrors((prev) => ({
                        ...prev,
                        selectedBudget: "",
                      }));
                    }
                  }}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih Budget yang Anda siapkan" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="300-400">300 - 400 juta</SelectItem>
                    <SelectItem value="400-500">400 - 500 juta</SelectItem>
                    <SelectItem value="500-600">500 - 600 juta</SelectItem>
                    <SelectItem value="600-700">600 - 700 juta</SelectItem>
                    <SelectItem value="700-800">700 - 800 juta</SelectItem>
                    <SelectItem value="800-900">800 - 900 juta</SelectItem>
                    <SelectItem value="900-1m">900 juta - 1 Miliar</SelectItem>
                    <SelectItem value="1m_keatas">
                      Lebih dari 1 Miliar
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formErrors.selectedBudget.length > 0 && (
                  <Text4
                    className="text-red-400 -mt-2"
                    text={formErrors.selectedBudget}
                  />
                )}
                {/* Input Timeline */}
                <label
                  htmlFor="timeline"
                  className="pt-[0.5rem] text-sm font-medium text-slate-900"
                >
                  Kapan rencana Anda bangun rumah?
                </label>{" "}
                <input
                  type="hidden"
                  name="timeline"
                  defaultValue={selectedTimeline}
                />
                <Select
                  value={selectedTimeline}
                  onValueChange={(value) => {
                    setSelectedTimeline(value);

                    // Reset error jika ada
                    if (formErrors.selectedTimeline) {
                      setFormErrors((prev) => ({
                        ...prev,
                        selectedTimeline: "",
                      }));
                    }
                  }}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih rencana waktu pembangunan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dalam_bulan_ini">
                      Dalam bulan ini
                    </SelectItem>
                    <SelectItem value="2_bulan_ini">
                      Dalam 2 bulan ini
                    </SelectItem>
                    <SelectItem value="3_bulan_ini">
                      Dalam 3 bulan ini
                    </SelectItem>
                    <SelectItem value="belum_tahu">Belum tahu</SelectItem>
                  </SelectContent>
                </Select>
                {formErrors.selectedTimeline && (
                  <Text4
                    className="text-red-400 -mt-2"
                    text={formErrors.selectedTimeline}
                  />
                )}
                {/* Message Input */}
                <section
                  className="rounded-md mt-[0.5rem] p-[1rem] w-full flex flex-col gap-3 text-start relative"
                  style={{
                    backgroundImage:
                      "url('https://ik.imagekit.io/didigi/didigi/pt-didigi-kreasi-solusindo/didigi_pt-digify-kreasi-solusindo_bg-wa_jasa-pembuatan-website-di-semarang.webp')",
                    backgroundSize: "cover", // Ensure the image covers the entire section
                    backgroundPosition: "center", // Center the image
                    backgroundRepeat: "no-repeat", // Prevent the image from repeating
                  }}
                >
                  <div className="absolute bg-slate-800 rounded-t-md text-white flex flex-row items-center min-h-[4.5rem] top-0 left-0 w-full px-[1rem] py-[1rem]">
                    <img
                      className="min-w-[3.5rem] max-w-[3.5rem] z-10 pr-[1rem]"
                      src="https://ik.imagekit.io/didigi/didigi/pt-didigi-kreasi-solusindo/didigi_pt-digify-kreasi-solusindo_bg-wa_jasa-pembuatan-website-di-semarang-whatsapp.webp"
                    />
                    <p className="text-lg">Admin Ayu</p>
                  </div>
                  {/* Content */}
                  <section className="relative z-10 flex flex-col">
                    <div className="pt-[5rem] ">
                      <p className="text-slate-300 text-sm text-center pb-3">
                        {getFormattedDate()}
                      </p>
                    </div>
                    <div className="bg-slate-300 w-fit px-3 py-2  rounded-r-lg rounded-t-lg">
                      <p className="text-sm">
                        Halo, saya Ayu. Ada yang bisa saya bantu?
                      </p>
                      <p className="text-end text-xs mt-2 text-slate-600">
                        {getFormattedTime()}
                      </p>
                    </div>
                    <div className="pt-[2rem] flex flex-row gap-2 items-center">
                      <textarea
                        ref={textareaRef}
                        name="message"
                        id="message"
                        placeholder="Tulis pesan Anda di sini"
                        value={inputMessage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= maxCharacters) {
                            setInputMessage(value);
                            if (value.trim() === "") {
                              setFormErrors((prev) => ({
                                ...prev,
                                message: "Pesan tidak boleh kosong",
                              }));
                            } else {
                              setFormErrors((prev) => ({
                                ...prev,
                                message: "",
                              }));
                            }
                          }
                        }}
                        className={`pl-4 text-slate-900 rounded-full w-full p-2 border  focus:ring-light-primary focus:border-light-primary ${
                          formErrors.message ? "border-red-500" : ""
                        }`}
                        rows={1}
                        style={{ overflow: "hidden", resize: "none" }}
                        maxLength={maxCharacters}
                        required
                      />
                      {/* Character Count */}
                      <button
                        type="submit"
                        className={`w-fit ${
                          isSubmitDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={handleSubmit}
                        disabled={
                          isSubmitDisabled || fetcher.state === "submitting"
                        }
                      >
                        {fetcher.state === "submitting" ? (
                          <div className="bg-green-600 p-2 rounded-full">
                            <Loader2 className="text-slate-300 animate-spin" />
                          </div>
                        ) : (
                          <div className="relative bg-green-600 p-2 rounded-full overflow-hidden">
                            {!isSubmitDisabled && (
                              <span className="shine absolute inset-0 z-0"></span>
                            )}
                            <SendHorizontal className="relative z-10 text-slate-300" />
                          </div>
                        )}
                      </button>
                    </div>
                    <div className="py-2 text-sm text-slate-500 text-start">
                      {inputMessage.length}/{maxCharacters}
                    </div>
                    {formErrors.message && (
                      <Text4
                        className="text-red-400 -mt-2"
                        text={formErrors.message}
                      />
                    )}
                    {!isFormValid && (
                      <Text4
                        className="text-red-400"
                        text="  Harap isi semua bidang untuk melanjutkan."
                      />
                    )}
                  </section>
                </section>
              </div>
            </fetcher.Form>

            {/* Modal */}
            <section className="p-[0.2rem] rounded-lg">
              {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                  <div className="flex flex-col justify-center bg-white p-8 mx-8 rounded-lg shadow-lg max-w-md text-center">
                    <img
                      className="self-center w-20"
                      src="https://ik.imagekit.io/01/sagopro/sagopro-success.webp?updatedAt=1719313103186"
                      alt="Success"
                    />
                    <Heading2
                      className="typo-light-primary font-semibold"
                      text="Berhasil !"
                    />
                    <Text4
                      className="typo-light-primary"
                      text="Terima kasih telah menghubungi kami. Kami akan mengirimkan jadwal konsultasi untuk Anda."
                    />
                    <button
                      onClick={closeModal}
                      className="item-image mt-4 bg-black inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-primary"
                    >
                      Tutup
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
